'use client';

import { useMemo, useRef } from 'react';
import { stegaClean } from '@sanity/client/stega';

import { GroqImage } from '@/helpers/Image';
import { cn } from '@/lib/utils';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichText, V3_HIGHLIGHT } from './richtext';
import {
  RevealBlock,
  RevealEyebrow,
  RevealRichText,
  RevealStyles,
  RevealWords,
  formatNumeric,
  parseNumeric,
} from './about/reveal';
import { StepsLoop, backOut, easeInOut, easeOut, seg, setText, useLoopFrame } from './steps/loop';

type Step = PageRizAffiliateSchema['stepsItems'][number];

const FALLBACK_BG: Record<string, string> = {
  account: 'linear-gradient(146deg, #1F8167 13.87%, #2A8C72 86.86%)',
  coupon: '#122823',
  payout: '#003828',
};

function GlassEdge({ className }: { className: string }) {
  return (
    <span
      aria-hidden
      className={cn('absolute inset-x-0 bottom-0 rounded-[inherit] border pointer-events-none max-lg:hidden', className)}
    />
  );
}

function AccountVisual({ step }: { step: Step }) {
  const rows = useMemo(() => step?.account?.rows ?? [], [step?.account?.rows]);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const fillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const amountRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const lead = Math.max(
    0,
    rows.findIndex((row) => row?.highlight),
  );
  const spec = useMemo(() => parseNumeric(rows[lead]?.amount), [rows, lead]);

  useLoopFrame((t) => {
    const v = easeInOut(seg(t, 0.3, 1.7)) * (1 - easeInOut(seg(t, 6.3, 7.2)));
    const pop = Math.sin(seg(t, 1.4, 2.1) * Math.PI);
    rows.forEach((_, i) => {
      const row = rowRefs.current[i];
      const fill = fillRefs.current[i];
      if (i === lead) {
        if (spec) {
          const step100 = spec.amount >= 10000 ? 100 : 1;
          const value = Math.round((spec.amount * (0.75 + 0.25 * v)) / step100) * step100;
          setText(amountRefs.current[i], formatNumeric(spec, value));
        }
        if (fill) fill.style.transform = `scaleX(${(0.55 + 0.45 * v).toFixed(4)})`;
        if (row) row.style.transform = `scale(${(1 + 0.012 * pop).toFixed(4)})`;
      } else {
        if (row) row.style.opacity = (1 - 0.35 * v).toFixed(3);
        if (fill) fill.style.opacity = (1 - 0.5 * v).toFixed(3);
      }
    });
  });

  if (!rows.length) return null;

  return (
    <div className="flex flex-col items-start w-full gap-[1.25rem]">
      {rows.map((row, i) => {
        const highlight = i === lead && !!row?.highlight;
        return (
          <div
            key={`${row?.tag ?? 'row'}-${i}`}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className={cn(
              'relative flex flex-col justify-center items-start w-full border-t border-white/[0.18] rounded-[0.5rem] gap-[1.25rem] p-[2.5rem]',
              highlight ? 'bg-white h-[15.375rem]' : 'bg-white/10 h-[11.5rem] lg:backdrop-blur-[0.5rem]',
            )}>
            {!highlight && <GlassEdge className="top-[-1px] border-[rgba(174,255,239,0.5)]" />}
            <div className="flex justify-between items-baseline w-full whitespace-nowrap">
              {row?.amount && (
                <p
                  className={cn(
                    'm-0 font-geist font-[500]',
                    highlight
                      ? 'text-black text-[4.5rem] tracking-[-0.035em] lg:text-[4rem] leading-[normal]'
                      : 'text-white/[0.62] text-[3rem] tracking-[-0.035em] leading-[normal]',
                  )}>
                  <span
                    ref={(el) => {
                      amountRefs.current[i] = el;
                    }}>
                    {row.amount}
                  </span>
                </p>
              )}
              {row?.tag && (
                <p
                  className={cn('m-0 font-geist font-[400] text-[1.75rem] leading-[normal]', highlight ? 'text-[#228E71]' : 'text-white/40')}>
                  {row.tag}
                </p>
              )}
            </div>
            <div
              className={cn(
                'flex overflow-clip w-full rounded-full',
                highlight ? 'h-[0.75rem] bg-black/10' : 'h-[0.5rem] bg-white/[0.18]',
              )}>
              <div
                ref={(el) => {
                  fillRefs.current[i] = el;
                }}
                className={cn('h-full rounded-full origin-left', highlight ? 'bg-[#228E71]' : 'bg-white/50')}
                style={{ width: `${row?.fillPercent ?? 0}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CouponVisual({ step }: { step: Step }) {
  const coupon = step?.coupon;
  const priceRef = useRef<HTMLSpanElement>(null);
  const strikeRef = useRef<HTMLElement>(null);
  const codeRef = useRef<HTMLSpanElement>(null);
  const caretRef = useRef<HTMLElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const from = useMemo(() => parseNumeric(coupon?.priceOriginal), [coupon?.priceOriginal]);
  const to = useMemo(() => parseNumeric(coupon?.priceCurrent), [coupon?.priceCurrent]);
  const code = stegaClean(coupon?.code) ?? '';

  useLoopFrame((t) => {
    const typed = Math.floor(seg(t, 0.4, 1.2) * (code.length + 0.01));
    const erased = Math.floor(seg(t, 6.6, 7.1) * (code.length + 0.01));
    setText(codeRef.current, code.slice(0, Math.max(0, typed - erased)));
    const typing = (t > 0.2 && t < 1.5) || (t > 6.5 && t < 7.3);
    if (caretRef.current) caretRef.current.style.opacity = typing && Math.floor(t * 5) % 2 === 0 ? '0.9' : '0';
    if (boxRef.current)
      boxRef.current.style.transform = `scale(${(1 + 0.03 * Math.sin(seg(t, 1.25, 1.9) * Math.PI)).toFixed(4)})`;
    const strike = easeOut(seg(t, 1.4, 1.9)) * (1 - easeInOut(seg(t, 6.5, 7.1)));
    if (strikeRef.current) strikeRef.current.style.transform = `scaleX(${strike.toFixed(4)})`;
    if (from && to) {
      const drop = easeOut(seg(t, 1.5, 3)) * (1 - easeInOut(seg(t, 6.6, 7.4)));
      setText(priceRef.current, formatNumeric(to, Math.round(from.amount - (from.amount - to.amount) * drop)));
    }
  });

  if (!coupon) return null;

  return (
    <div className="relative flex flex-col justify-center items-center w-full whitespace-nowrap rounded-[0.5rem] gap-[1rem] bg-black/[0.13] h-[26rem] lg:h-[27.5rem] lg:backdrop-blur-[6.25rem]">
      <GlassEdge className="top-0 border-[rgba(212,225,217,0.3)]" />
      {coupon?.priceOriginal && (
        <p className="m-0 font-geist font-[400] text-white/50 text-[2.5rem] leading-[normal]">
          <s className="relative inline-block no-underline">
            {coupon.priceOriginal}
            <i
              ref={strikeRef}
              aria-hidden
              className="absolute left-[-0.25rem] right-[-0.25rem] top-[52%] h-[0.1875rem] bg-current origin-left"
            />
          </s>
        </p>
      )}
      {coupon?.priceCurrent && (
        <p className="m-0 font-geist font-[500] text-[#CFFFDB] text-[7.5rem] leading-[7rem] tracking-[-0.05em] lg:text-[7rem] lg:leading-[0.92]">
          <span ref={priceRef}>{coupon.priceCurrent}</span>
        </p>
      )}
      {code && (
        <div
          ref={boxRef}
          className="flex items-center rounded-[1rem] border border-dashed border-white/40 gap-[2rem] px-[2.5rem] py-[1.75rem] font-geist text-[1.75rem] leading-[normal]">
          {coupon?.codeLabel && <p className="m-0 font-[400] text-white/[0.62]">{coupon.codeLabel}</p>}
          <p className="m-0 grid font-[500] text-white">
            <span
              aria-hidden
              className="invisible col-start-1 row-start-1">
              {code}
            </span>
            <span className="col-start-1 row-start-1">
              <span ref={codeRef}>{coupon.code}</span>
              <i
                ref={caretRef}
                aria-hidden
                className="inline-block align-[-0.125rem] w-[0.1875rem] h-[1.75rem] ml-[0.25rem] mr-[-0.4375rem] bg-current opacity-0"
              />
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

function PayoutVisual({ step }: { step: Step }) {
  const payout = step?.payout;
  const amountRef = useRef<HTMLSpanElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLSpanElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const spec = useMemo(() => parseNumeric(payout?.amount), [payout?.amount]);

  useLoopFrame((t) => {
    const out = easeInOut(seg(t, 6.7, 7.4));
    if (spec) setText(amountRef.current, formatNumeric(spec, Math.round(spec.amount * easeOut(seg(t, 0.3, 1.8)) * (1 - out))));
    rowRefs.current.forEach((row, i) => {
      if (!row) return;
      const start = 1.1 + 0.2 * i;
      const p = easeOut(seg(t, start, start + 0.8)) * (1 - out);
      row.style.opacity = p.toFixed(3);
      row.style.transform = `translateY(${((1 - p) * 1).toFixed(3)}rem)`;
    });
    if (pillRef.current) {
      pillRef.current.style.opacity = (seg(t, 1.9, 2.2) * (1 - out)).toFixed(3);
      pillRef.current.style.transform = `scale(${((0.86 + 0.14 * backOut(seg(t, 1.9, 2.5))) * (1 - 0.06 * out)).toFixed(4)})`;
    }
    if (dotRef.current)
      dotRef.current.style.transform = `scale(${(1 + 0.28 * Math.max(0, Math.sin((t - 2.4) * 2.2))).toFixed(3)})`;
  });

  if (!payout) return null;

  return (
    <div className="flex flex-col items-start w-full bg-white rounded-[1rem] gap-[1.5rem] p-[2.5rem]">
      <div className="flex justify-between items-center w-full">
        {payout?.headerLabel && (
          <p className="m-0 whitespace-nowrap font-geist font-[400] text-[#001B14]/[0.42] text-[1.75rem] leading-[normal]">
            {payout.headerLabel}
          </p>
        )}
        {payout?.statusLabel && (
          <div
            ref={pillRef}
            className="flex items-center origin-right rounded-full bg-[#1B8869]/[0.12] gap-[0.75rem] px-[1.5rem] py-[0.75rem]">
            <span
              ref={dotRef}
              className="rounded-full shrink-0 bg-[#1B8869] w-[0.75rem] h-[0.75rem]"
            />
            <p className="m-0 whitespace-nowrap font-geist font-[500] text-[#003828] text-[1.75rem] leading-[normal]">
              {payout.statusLabel}
            </p>
          </div>
        )}
      </div>
      {payout?.amount && (
        <p className="m-0 whitespace-nowrap font-geist font-[500] text-[#001B14] text-[4rem] leading-[normal] tracking-[-0.04em]">
          <span ref={amountRef}>{payout.amount}</span>
        </p>
      )}
      {!!payout?.rows?.length && (
        <div className="flex flex-col items-start w-full gap-[1rem] text-[1.75rem] leading-[normal]">
          {payout.rows.map((row, i) => (
            <div
              key={`${row?.label ?? 'row'}-${i}`}
              ref={(el) => {
                rowRefs.current[i] = el;
              }}
              className="flex justify-between items-start w-full border-t border-[#001B14]/[0.07] py-[1.25rem]">
              <p className="m-0 font-geist font-[400] text-[#001B14]/50">{row?.label}</p>
              <p className="m-0 font-geist font-[500] text-[#001B14]">{row?.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function lines(value?: string) {
  return (stegaClean(value) ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function StepCard({ step, index }: { step: Step; index: number }) {
  const variant = stegaClean(step?.variant) ?? 'account';
  const titleLines = lines(step?.title);
  const sceneRef = useRef<HTMLDivElement>(null);

  useLoopFrame((t) => {
    if (variant === 'coupon' && sceneRef.current)
      sceneRef.current.style.transform = `scale(${(1.015 + 0.015 * Math.sin((t / 8) * Math.PI * 2)).toFixed(4)})`;
  });

  return (
    <RevealBlock
      index={index}
      className="flex flex-col flex-1 items-start w-full gap-[2rem] lg:gap-[3rem]">
      <div
        className="rz-steps-card relative overflow-clip w-full rounded-[0.75rem] h-[47.75rem] lg:h-[55.125rem]"
        style={{ background: FALLBACK_BG[variant] ?? FALLBACK_BG.account }}>
        {step?.image && (
          <div
            ref={sceneRef}
            className="absolute inset-0">
            <GroqImage
              src={step.image}
              alt=""
              fill
              quality={100}
              sizes="(min-width: 993px) 20vw, 93vw"
              style={{ width: '100%', height: '100%' }}
              className="object-cover pointer-events-none"
            />
          </div>
        )}

        <div className="flex absolute inset-0 justify-center items-center p-[2rem] lg:items-start lg:pt-[8.125rem]">
          <div
            className={cn(
              'flex flex-col items-center max-w-full',
              variant === 'coupon' ? 'w-[37rem]' : 'w-[41rem]',
              variant === 'account' ? 'gap-[4rem] lg:gap-[2.125rem]' : 'gap-[2.125rem]',
            )}>
            <div className={cn('flex flex-col items-center text-center', variant === 'payout' ? 'gap-0' : 'gap-[0.5rem]')}>
              {(step?.number || step?.label) && (
                <div className="flex items-center whitespace-nowrap font-geist font-[400] text-white/[0.62] text-[1.75rem] leading-[normal] gap-[0.75rem]">
                  {step?.number && <p className="m-0">{step.number}</p>}
                  {step?.label && <p className="m-0">{step.label}</p>}
                </div>
              )}
              {titleLines.length > 0 && (
                <p className={cn('m-0 font-geist font-[500] text-white text-[3rem] tracking-[-0.03em]', titleLines.length > 1 ? 'leading-[3.625rem]' : 'leading-[normal]')}>
                  {titleLines.map((line, i) => (
                    <span
                      key={`${line}-${i}`}
                      className="block">
                      {line}
                    </span>
                  ))}
                </p>
              )}
            </div>

            {variant === 'account' && <AccountVisual step={step} />}
            {variant === 'coupon' && <CouponVisual step={step} />}
            {variant === 'payout' && <PayoutVisual step={step} />}
          </div>
        </div>
      </div>

      {(step?.captionTitle || step?.captionText) && (
        <div className="flex flex-col items-start w-full gap-[1.25rem]">
          {step?.captionTitle && (
            <p className={cn('m-0 w-full font-geist font-[500] text-[#001B14] text-[2.75rem] leading-[3.375rem] tracking-[-0.03em]', variant === 'coupon' && 'lg:text-[3rem]', 'lg:leading-[1.2]')}>
              {step.captionTitle}
            </p>
          )}
          <RevealWords
            text={step?.captionText}
            className="m-0 w-[min(43rem,100%)] whitespace-pre-line font-geist font-[400] text-[#001B14]/60 text-[2.25rem] leading-[1.5] tracking-[-0.01em]"
          />
        </div>
      )}
    </RevealBlock>
  );
}

export default function StepsSection({ data }: { data: PageRizAffiliateSchema }) {
  const sectionRef = useRef<HTMLElement>(null);

  if (!data?.stepsChip && !data?.stepsHeading?.length && !data?.stepsItems?.length) return null;

  return (
    <section
      ref={sectionRef}
      className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:py-[14rem]">
      <RevealStyles />
      <style
        jsx
        global>{`
        .rz-steps-card {
          transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @media (hover: hover) and (prefers-reduced-motion: no-preference) {
          .rz-steps-card:hover {
            transform: translateY(-0.5rem);
          }
        }
      `}</style>
      <div className="flex flex-col mx-auto w-[min(150rem,100%)]">
        <div className="flex flex-col items-center w-full gap-[2rem]">
          <RevealEyebrow text={data?.stepsChip} />
          <RevealRichText
            value={data?.stepsHeading}
            components={measuredRichText}
            className={`${V3_HIGHLIGHT} richtext richtext-center w-[min(38rem,100%)] lg:w-[min(51.25rem,100%)] prose-headings:text-[#001B14] prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[4.5rem] prose-headings:leading-[1.08] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-ctr:0.1834rem] [--hl-pl:0rem] [--hl-pr:1.075rem] [--hl-ext:0rem] [--hl-wash:4.875rem] [--hl-dy:0.4rem] lg:[--hl-ctr:0.13rem] lg:[--hl-pl:0.7875rem] lg:[--hl-pr:1.05rem] lg:[--hl-wash:6.75rem] lg:[--hl-dy:0.3rem]`}
          />
        </div>

        {data?.stepsItems?.length > 0 && (
          <StepsLoop root={sectionRef}>
            <div className="flex flex-col items-start w-full gap-[5rem] pt-[4.375rem] lg:flex-row lg:gap-[3rem] lg:pt-[4.625rem]">
              {data.stepsItems.map((step, i) => (
                <StepCard
                  key={`${step?.captionTitle ?? 'step'}-${i}`}
                  step={step}
                  index={i}
                />
              ))}
            </div>
          </StepsLoop>
        )}
      </div>
    </section>
  );
}
